import { authFetch, getApiBase, tokenStore } from "./auth";

export interface SiggeCase {
  id: number;
  title: string;
  status: string;
  client: { full_name: string; email: string } | null;
}

export interface AskResponse {
  answer: string;
  sources: Array<{
    n: number;
    document_title: string;
    page: number | null;
    text: string;
  }>;
  model: string;
  input_tokens: number;
  output_tokens: number;
}

function requireAuth(): void {
  if (!tokenStore.access) throw new Error("Inte inloggad mot Sigge.");
}

async function readError(res: Response): Promise<string> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      const data = (await res.json()) as { detail?: string };
      if (data?.detail) return data.detail;
    } catch {
      /* fall through */
    }
  }
  return `HTTP ${res.status}`;
}

export async function searchCases(query: string): Promise<SiggeCase[]> {
  requireAuth();
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  const res = await authFetch(`${getApiBase()}/api/cases/?${params}`);
  if (!res.ok) throw new Error(await readError(res));
  const data = (await res.json()) as { results: SiggeCase[] };
  return data.results;
}

/**
 * Ställer en fråga mot ett ärende — samma /api/kb/ask som portalen
 * använder. Word-add-in:et lägger till "Aktuellt Word-dokumentet:" +
 * dokument-extrakt i frågan så LLM:en kan resonera om båda källorna.
 */
export async function ask(
  caseId: number,
  question: string
): Promise<AskResponse> {
  requireAuth();
  const res = await authFetch(`${getApiBase()}/api/kb/ask/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ case: caseId, question, limit: 5 }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as AskResponse;
}

// ── Tidsloggning ────────────────────────────────────────────────
// Samma endpoint + samma payload-shape som Outlook-add-in:et
// använder — juristen ska kunna logga tid från Word medan hen
// jobbar i ett dokument, utan att växla till portalen eller Outlook.

export type ActivityType =
  | "radgivning"
  | "dokgranskning"
  | "upprattande"
  | "mote"
  | "ovrigt";

export const ACTIVITY_LABELS: Record<ActivityType, string> = {
  radgivning: "Rådgivning",
  dokgranskning: "Dokumentgranskning",
  upprattande: "Upprättande",
  mote: "Möte",
  ovrigt: "Övrigt",
};

export interface TimeEntryPayload {
  case: number;
  activity_type: ActivityType;
  duration_minutes: number;
  note?: string;
  billable?: boolean;
}

export async function logTime(
  payload: TimeEntryPayload
): Promise<{ id: number }> {
  requireAuth();
  const res = await authFetch(`${getApiBase()}/api/time-entries/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as { id: number };
}
